/* eslint-disable no-await-in-loop */
/* eslint-disable max-len */
import {
    observable, reaction, isObservableArray, makeObservable, action,
} from 'mobx'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18next'
import lodash from 'lodash'
import * as moment from 'moment-timezone'
import Enums from './Enum'
import {
    CountryListQuestion,
    DropdownListQuestion,
    GridQuestion,
    SmartGridQuestion,
    MultipleChoiceQuestion,
    NumericQuestion,
    PercentageQuestion,
    PriorityQuestion,
    RatingQuestion,
    SingleChoiceQuestion,
    StaticText,
    TextAreaQuestion,
    TextBoxQuestion,
    QuestionLang,
    OptionSet,
    UrlVariableQuestion,
    TextBoxListQuestion,
    FormQuestion,
    DateQuestion,
    NumberFormQuestion,
} from '../Models/Question'
import SkipLogic from '../Models/SkipLogic'
import { unescapeHtmlTags } from '../utils/helpers'
import createFormFields from './form/FieldDefinition'
import questionFields from './form/QuestionFields'

const {
    questionType, contextOidLevel, leftRight, skipLogicStatus,
} = Enums

export default class QuestionBuilderStore {
    host = ''

    @observable dropdownType

    @observable saveDisabled = false

    @observable optionTab = 0

    @observable optionTabX = 0

    @observable smartGridRows = new OptionSet({})

    @observable showHistoricDataModal = false

    @observable selectedCategory = {}

    @observable prepopulateSelection

    @observable countries = []

    @observable selectedLocale

    @observable availableLocales = []

    @observable questionRespondents = []

    @observable builderLoading = true

    @observable tab = 'standard'

    survey

    @observable question

    @observable contextOid

    @observable lang

    @observable questionTypes = []

    @observable disabledQuestionType

    @observable isEdit = false

    @observable showEditModal = false

    @observable showOptionEditModal = false

    @observable showSkipLogicAlertModal = false

    @observable showProductTypeAlertModal = false

    @observable showUsedInOtherSurveysModal = false

    @observable listOfOtherSurveys = []

    @observable subQueOptionId = undefined

    @observable editField = undefined

    @observable parentId

    @observable inheritedOptions = []

    @observable subQuestionMap = observable.map({})

    @observable tabName = 'standard'

    @observable formFields = []

    @observable fallbackTranslations = {
        shortTitle: undefined,
        longTitle: undefined,
        instructions: undefined,
        aboveText: undefined,
        belowText: undefined,
        fieldsToHideBlankTranslation: ['instructions', 'belowText', 'aboveText'],
    }

    switchFormatOptions = [
        {
            label: i18n.t('q_switch_format_opt_m2ToF2'),
            value: {
                value: 'm2Toft2',
                left: 'M2',
                right: 'Ft2',
            },
        },
        {
            label: i18n.t('q_switch_format_opt_kmToMile'),
            value: {
                value: 'kmTomile',
                left: 'Km',
                right: 'Mile',
            },
        },
        {
            label: i18n.t('q_switch_format_opt_kgToLb'),
            value: {
                value: 'kgTolb',
                left: 'Kg',
                right: 'Pounds',
            },
        },
        {
            label: i18n.t('q_switch_format_opt_estimate'),
            value: {
                value: 'estimate',
                left: 'Exact',
                right: 'Estimate',
            },
        },
        {
            label: i18n.t('q_switch_format_opt_custom'),
            value: {
                value: '',
                left: '',
                right: '',
            },
        },
    ]

    @observable switchFormat = undefined

    validationOptions = [
        {
            value: 'email',
            label: 'Email',
        },
    ]

    formatOptions = [
        {
            value: 'Number',
            label: 'Number',
        },
        {
            value: '%',
            label: '%',
        },
    ]

    @observable locale

    regFields = []

    @observable valueHolder = observable.map({})

    @observable subQuestionType = null

    customFormSubQuestionType = [
        {
            value: questionType.TextBox,
            label: i18n.t(`questionType.${questionType.TextBox}`),
        },
        {
            value: questionType.DropdownList,
            label: i18n.t(`questionType.${questionType.DropdownList}`),
        },
        {
            value: questionType.NumberRange,
            label: i18n.t(`questionType.${questionType.NumberRange}`),
        },
        {
            value: questionType.Date,
            label: i18n.t(`questionType.${questionType.Date}`),
        },
    ]

    @observable accState = {
        std: true,
        adv: true,
        pro: true,
        editQuestionDisabled: false,
    }

    @observable optionEditorLoader = false

    @observable optInd

    @observable optKey

    @observable isAccClose = {}

    questionTemplateOptions = [
        {
            label: 'Maxbi Objective Selection (CH)',
            value: 'ObjectiveSelection',
        },
    ]

    parentQuestions = []

    parentOptions = {}

    constructor(store, survey) {
        makeObservable(this)
        this.appStore = store
        this.survey = survey
        this.checkUrl()

        reaction(
            () => this.question,
            (question) => {
                this.disabledQuestionType = Boolean(
                    question.id
                        && (
                            question.liveResponses
                            || question.metadata.oid < 6
                            || !['RD', 'CH', 'SB', 'PC', 'OD', 'TL', 'UV'].includes(question.questionTypeCode)
                        ),
                )

                this.sgDisable = (
                    this.question.questionTypeCode === 'SG'
                    && ((this.contextOid === contextOidLevel.SURVEY_BUILDER
                        && this.question.metadata.oid === 1)
                    || ['BEH', 'COG'].includes(this.question.metadata.kpiType))
                )
            },
        )

        reaction(
            () => [R.pathOr(false, ['settings', 'frontend', 'switch'], this.question), R.pathOr(false, ['settings', 'frontend', 'info'], this.question)],
            ([switchArr, info]) => {
                this.switchFormat = switchArr && this.getSwitchFormat(this.switchFormatOptions)
                this.setValueHolder({ value: Boolean(switchArr), name: 'settings.frontend.switch.check' })
                this.setValueHolder({ value: switchArr, name: 'settings.frontend.switch' })
                if (switchArr) {
                    this.setValueHolder({ value: this.switchFormat.value.left, name: 'settings.frontend.switch.0' })
                    this.setValueHolder({ value: this.switchFormat.value.right, name: 'settings.frontend.switch.1' })
                }
                this.setValueHolder({ value: Boolean(info), name: 'settings.frontend.info.check' })
            },
        )

        reaction(
            () => this.appStore.skipLogicModalVisible,
            (skipLogicModalVisible) => {
                if (skipLogicModalVisible) return
                const { option, optionSetName } = this.appStore.skipLogicConfig
                const updatedOptionId = option.id
                const optionSet = this.getValueHolder(optionSetName)
                const updatedOption = this.setSkipLogicStatus(option)
                const updatedOptions = optionSet.options.map(
                    (opt) => (opt.id === updatedOptionId ? updatedOption : opt),
                )
                this.setValueHolder(
                    { value: { ...optionSet, options: updatedOptions }, name: optionSetName },
                )
            },
        )

        reaction(
            () => [
                this.tabName,
                Array.from(this.valueHolder),
                Array.from(this.subQuestionMap),
                this.accState,
                this.questionRespondents,
                this.isAccClose,
                this.selectedLocale,
            ],
            () => {
                if (R.hasPath(['questionTypeCode'], this.question)) {
                    this.createForm()
                }
            },
        )
    }

    setLanguages = () => {
        const { appStore, survey } = this
        const subUrl = process.env.ENVIRONMENT === 'production' ? 'explori' : 'staging.explori'

        const isSurveyBuilder = this.contextOid === contextOidLevel.SURVEY_BUILDER
        const surveyLocales = R.pathOr([], ['metadata', 'locales'], survey)
        const companyLocales = R.pathOr([], ['localeOptions'], appStore)
        const locales = isSurveyBuilder ? surveyLocales : companyLocales

        this.allLangs = new Map(companyLocales.map(
            ({ value, label }) => [value, { label, src: `https://surveys.${subUrl}.com/assets/images/flags/${value}.png` }],
        ))
        this.availableLocales = locales.map((value) => {
            let val = value
            if (!isSurveyBuilder && value instanceof Object) val = value.value
            const obj = this.allLangs.get(val)
            return obj ? { value: val, label: obj.label, src: obj.src } : null
        }).filter(Boolean) // eslint warn

        this.defaultLocale = isSurveyBuilder ? appStore.surveyLocale : appStore.langcode

        let defaultLang
        let value

        if (this.allLangs.has(this.defaultLocale)) {
            defaultLang = this.allLangs.get(this.defaultLocale)
            value = this.defaultLocale
        } else if (this.allLangs.has(appStore.userData.locale)) {
            defaultLang = this.allLangs.get(this.defaultLocale)
            value = this.defaultLocale
        } else {
            value = appStore.clientLanguages[0].langcode
            defaultLang = this.allLangs.get(value)
        }

        const currentLang = { value, label: defaultLang.label, src: defaultLang.src }
        this.changeLanguage(currentLang)
    }

    initStart = () => {
        const { appStore } = this

        this.questionIndex = appStore.questionDetails.questionIndex
        this.pageIndex = appStore.questionDetails.pageIndex
        this.contextOid = appStore.questionDetails.contextOid || contextOidLevel.SURVEY_BUILDER
        this.prevQuestions = appStore.questionDetails.prevQuestions || []
        this.sgQuestions = appStore.questionDetails.sgQuestions
        this.selectedCategory = R.pathOr([], ['questionDetails', 'questionCategories'], appStore).find(
            (category) => category.value === appStore.questionDetails.categoryId,
        ) || {}

        this.setLanguages()
        this.setMergeFields()
        this.fetchMetrics()
    }

    initEnd = () => {
        const { appStore, survey } = this
        this.question.inheritVisible = R.pathOr(0, ['questionDetails', 'pageIndex'], appStore) > 1
        this.question.store = this

        if (this.contextOid <= 5 || this.contextOid === contextOidLevel.CUSTOM_CATEGORY) {
            this.productTypeCode = appStore.questionDetails.productType
            this.portfolioId = appStore.questionDetails.portfolioId
            this.surveyTypeCode = appStore.questionDetails.surveyType
            this.respondentTypeCode = appStore.questionDetails.respondentType
            this.industryTypeCode = appStore.questionDetails.industryType
            this.sgKpis = appStore.questionDetails.sgKpis
        } else if (survey) {
            const { productType, industry } = survey.metadata.productInfo.industrySpecificInfo
            this.question.surveyId = survey.id
            this.question.productId = survey.metadata.productInfo.productId
            this.question.productTypeCode = productType
            this.question.surveyTypeCode = survey.metadata.taxonomy.surveyType
            this.question.respondentTypeCode = survey.metadata.taxonomy.respondentType
            this.question.industryTypeCode = industry
        }

        this.findParentQuestions()

        this.questionTypes = this.getQuestionTypes(
            this.contextOid, this.question, this.industryTypeCode,
        )
        this.setObjInMap(this.question, this.valueHolder)
    }

    init = async () => {
        this.builderLoading = true
        const { appStore } = this

        this.isEdit = R.hasPath(['questionDetails', 'questionId'], appStore)

        try {
            this.initStart()
            const isSurveyBuilder = this.contextOid === contextOidLevel.SURVEY_BUILDER

            // edit question
            if (this.isEdit) {
                this.pageQuestionId = appStore.questionDetails.pageQuestionId
                this.usedInOtherSurveys = appStore.questionDetails.usedInOtherSurveys
                this.usedInParentSurvey = appStore.questionDetails.usedInParentSurvey
                // get selected question data to edit
                this.question = await this.populateQuestion(appStore.questionDetails.questionId)

                // run below only on Survey Builder flow where inherit options will be visible
                if (this.contextOid === contextOidLevel.SURVEY_BUILDER) {
                    this.parentId = this.appStore.questionDetails.parentId
                    if (this.parentId && this.parentId.label !== '') {
                        // set inherit options from the inherited question
                        const inheritPageQuestion = this.getPageQuestionFromPageId(
                            this.parentId.value,
                        )
                        this.addInheritOptions(inheritPageQuestion.question)
                    }
                    const [pageQuestion, questionIndex] = this.getPageQuestion(
                        this.appStore.questionDetails.pageQuestionId, this.pageIndex,
                    )
                    this.pageQuestion = pageQuestion
                    this.questionIndex = questionIndex
                }

                if (this.contextOid === contextOidLevel.QUESTION_EDITOR_CLIENT) {
                    this.questionRespondents = await this.getRespondent(appStore.questionDetails.questionId)
                }

                // close the modal if question is undefined
                if (!this.question) this.closeModal()
            } else {
                // add new question
                // initiate with question type RD
                this.toggleTab('newQuestion')
                this.question = new SingleChoiceQuestion({
                    metadata: {
                        isMandatory: true,
                        createdByUserId: appStore.tokenData.userId,
                        oid: appStore.questionDetails.oid,
                        clientId: appStore.tokenData.clientId,
                        categoryId: appStore.questionDetails.categoryId,
                        isGlobal: (
                            appStore.questionDetails.oid <= 3 && appStore.tokenData.clientId === 1
                        ),
                        benchmark: true,
                        isEditable: true,
                    },
                    optionSet: {},
                    content: {
                        shortTitle: new Map([[this.defaultLocale, new QuestionLang({})]]),
                    },
                    settings: {
                        frontend: {},
                        report: {},
                    },
                })
                this.selectedLocale = isSurveyBuilder ? this.survey.metadata.defaultLocale : appStore.langcode
            }

            this.initEnd()

            if (this.contextOid === contextOidLevel.QUESTION_EDITOR_CLIENT) {
                this.productMap = await this.getProdMap()
            }
        } catch (error) {
            console.log('initQuestionBuilderStore ', error)
        }
        this.builderLoading = false
    }

    initSubQuestion = (subQueType, question) => {
        const { appStore } = this
        try {
            this.initStart()

            // set subQuestion for edit
            if (question) {
                this.question = question
            } else {
                const queObj = {
                    metadata: {
                        isMandatory: true,
                        createdByUserId: appStore.tokenData.userId,
                        oid: appStore.questionDetails.oid,
                        clientId: appStore.tokenData.clientId,
                        categoryId: appStore.questionDetails.categoryId,
                        isGlobal: (
                            appStore.questionDetails.oid <= 3 && appStore.tokenData.clientId === 1
                        ),
                        benchmark: true,
                        isEditable: true,
                    },
                    optionSet: {},
                    content: {
                        shortTitle: new Map([[this.defaultLocale, new QuestionLang({ text: '' })]]),
                    },
                    settings: {
                        frontend: {},
                        report: {},
                    },
                }

                if (subQueType === questionType.NumberRange) {
                    this.question = new NumericQuestion({
                        metadata: queObj.metadata,
                        content: queObj.content,
                        settings: queObj.settings,
                    })
                } else {
                    this.question = new DropdownListQuestion({
                        metadata: queObj.metadata,
                        optionSet: queObj.optionSet,
                        content: queObj.content,
                        settings: queObj.settings,
                    })
                    this.question.optionSet.axis = 'y'
                }
            }

            this.initEnd()
        } catch (error) {
            console.log('initSubQuestion ', error)
        }
    }

    validateRespondents = (questionRespondentInfo) => {
        const isNilOrEmpty = (val) => R.isEmpty(val) || R.isNil(val)
        if (isNilOrEmpty(questionRespondentInfo)) return false
        const validateArgs = (arr) => !arr.some((el) => isNilOrEmpty(el))
        return validateArgs(questionRespondentInfo)
    }

    createForm = () => {
        this.formFields = createFormFields(
            this,
            this.question.questionTypeCode,
            questionFields,
            this.tabName,
        )
    }

    setTabs = () => {
        const { contextOid, question } = this
        const showAdvancedTab = !question ? false : (
            (question.questionTypeCode !== questionType.UrlVariable)
            && (question.questionTypeCode !== questionType.SmartGrid
            || contextOid !== contextOidLevel.SURVEY_BUILDER
            || question.metadata.oid !== 1))
        const showTranslate = !!question && process.env.TRANSLATIONS
        this.showProductTypesTab = (contextOid === contextOidLevel.QUESTION_EDITOR_CLIENT)

        return [
            {
                name: 'newQuestion',
                reachable: true,
                hidden: true, // hidden from UI
            },
            {
                label: i18n.t('tab_standard'),
                name: 'standard',
                reachable: true,
            },
            {
                label: i18n.t('tab_advanced'),
                name: 'advanced',
                reachable: showAdvancedTab,
            },
            {
                label: i18n.t('translation.title'),
                name: 'translate',
                reachable: showTranslate,
            },
            {
                label: i18n.t('tab_product_types'),
                name: 'productTypes',
                reachable: this.showProductTypesTab,
            },
        ].filter((tab) => (tab.reachable))
    }

    @action
    toggleTab = (tab) => {
        if (this.tabName !== tab) this.tabName = tab
    }

    checkUrl = () => {
        const host = window.location.hostname
        this.host = window.location.hostname
        const purpose = host.split('.')[0]
        if (purpose.match(/dev/)) {
            this.gateway = `gateway.${purpose}.explori.com` // window.location.hostname
        } else if (purpose.match(/qa/)) {
            this.gateway = 'qa-api.qa.hosting.explori.com'
        } else if (host === 'tasks.v3.explori.com') {
            this.gateway = 'gateway.explori.com'
        } else {
            const parts = this.host.split('.')
            parts[0] = 'gateway'
            this.gateway = parts.join('.')
        }
    }

    changeQuestionCategory = (obj) => {
        console.log(`changeQuestionCategory ${JSON.stringify(obj, null, 4)}`)
        this.selectedCategory = obj
        this.question.metadata.categoryId = obj.value
    }

    getProdMap = async () => {
        const { appStore } = this
        const { clientId } = appStore.userData
        try {
            const productMaps = await appStore.http.get(`/productmap/${clientId}`)
            const prodMap = productMaps.reduce((obj, m) => {
                let newObj = { ...obj }

                const productLens = R.lensPath([m.producttypecode])
                newObj = R.set(productLens, R.or(R.view(productLens, newObj),
                    {
                        label: m.producttypename,
                        value: m.producttypecode,
                        indKey: m.industrycode,
                        children: {},
                    }), newObj)

                const surveyLens = R.lensPath([m.producttypecode, 'children', m.surveytypecode])
                newObj = R.set(surveyLens, R.or(R.view(surveyLens, newObj),
                    { label: m.surveytypename, value: m.surveytypecode, children: {} }), newObj)

                const respondentLens = R.lensPath(
                    [m.producttypecode, 'children', m.surveytypecode, 'children', m.respondenttypecode],
                )
                newObj = R.set(respondentLens, R.or(R.view(respondentLens, newObj),
                    { label: m.respondenttypename, value: m.respondenttypecode }), newObj)

                return newObj
            }, {})
            return prodMap
        } catch (error) {
            console.log('getProdMap error ', error)
            return []
        }
    }

    getRespondent = async (questionId) => {
        const { appStore } = this
        try {
            let respondents = await appStore.http.get(`/question/${questionId}/respondents`)
            respondents = respondents.map(({
                industrycode,
                producttypecode,
                surveytypecode,
                respondenttypecode,
            }) => (
                `${industrycode}-${producttypecode}-${surveytypecode}-${respondenttypecode}`
            ))
            return respondents
        } catch (error) {
            console.log('getRespondent error ', error)
            return []
        }
    }

    changeProduct = (value, respCodes) => {
        console.log('>> change ', value, respCodes)
        if (value) {
            this.questionRespondents = [...this.questionRespondents, ...respCodes]
        } else {
            this.questionRespondents = this.questionRespondents.filter((qRes) => (!respCodes.includes(qRes)))
        }
    }

    setInheritQuestion = (obj) => {
        this.parentId = obj || { value: '00000000-0000-0000-0000-000000000000', label: '' }
        if (this.parentId.label === '') return
        const inheritPageQuestion = this.getPageQuestionFromPageId(this.parentId.value)
        this.addInheritOptions(inheritPageQuestion.question)
    }

    getPageQuestionFromPageId = (pageId) => {
        const pageQuestions = []
        this.appStore.survey.pages.forEach((page) => {
            if (page.questions) {
                pageQuestions.push(...page.questions)
            }
        })
        return pageQuestions.find(
            (pq) => pq.id === pageId,
        )
    }

    addInheritOptions = (question) => {
        if (!question) return
        // get options from inherited question
        this.inheritedOptions = R.pathOr(question.optionSet, ['optionSetY'], question).options
            .filter((option) => (!option.isNonReporting))
    }

    isNewOption = (option) => option.isNew

    persistQuestion = async (clone = false) => {
        const store = this
        const app = this.appStore
        const { question } = store
        store.saveDisabled = true

        const json = {
            contextOid: store.contextOid,
            question: question.toJSON(),
        }

        if (!json.question.metadata.initialLocale) {
            json.question.metadata.initialLocale = store.defaultLocale
        }

        if (question.questionTypeCode === 'FM' || question.questionTypeCode === 'NF') {
            json.question.optionSet = Array.from(this.subQuestionMap.values()).map(({ isMandatory, store }) => {
                const questionJson = store.question.toJSON()
                questionJson.metadata.categoryId = json.question.metadata.categoryId
                questionJson.metadata.initialLocale = json.question.metadata.initialLocale
                questionJson.metadata.oid = json.question.metadata.oid
                return {
                    mandatory: isMandatory,
                    question: questionJson,
                }
            })
        }

        if (store.contextOid <= 5) {
            json.portfolioId = store.portfolioId
            json.productType = store.productTypeCode
            json.surveyType = store.surveyTypeCode
            json.respondentType = store.respondentTypeCode
            json.industryType = store.industryTypeCode
            json.isMandatory = question.metadata.isMandatory // store.mandatory
            json.surveyMandatory = true
        }
        if (store.contextOid === contextOidLevel.SURVEY_BUILDER) {
            json.surveyId = store.survey.id
            json.pageQuestionId = app.questionDetails.pageQuestionId
            json.cloneQuestion = !!clone
        }
        if (question.id) {
            if (question.questionTypeCode === 'SG'
                && (
                    (store.contextOid === contextOidLevel.SURVEY_BUILDER && question.metadata.oid !== 7)
                    || this.appStore.isTemplate
                )
            ) {
                app.addNotification(null, 'Question updated', 'success')
                store.builderRefreshQuestion({}, question.questionTypeCode)
                store.closeModal()
            } else {
                try {
                    const response = await app.http.put(`/api/question/${question.id}`, JSON.stringify(json))
                    await this.saveRespondents(question.id)
                    app.addNotification(null, 'Question updated', 'success')
                    store.builderRefreshQuestion(response, question.questionTypeCode, clone)
                    store.closeModal()
                } catch (error) {
                    console.log(error)
                    store.closeModal()
                    app.addNotification(null, 'Error updating questions', 'error')
                }
            }
        } else {
            try {
                const response = await app.http.post('/api/question', JSON.stringify(json))
                const questionId = response.id
                question.questionId = questionId
                await this.saveRespondents(response.newId || response.id)
                app.addNotification(null, 'Question created', 'success')
                store.builderAddQuestion()
                store.closeModal()
            } catch (error) {
                console.log(error)
                app.addNotification(null, error, 'error')
                store.saveDisabled = false
            }
        }
    }

    saveQuestion = async () => {
        try {
            if (this.saveDisabled) return
            if (!this.validate()) return
            if (this.questionRespondents.length === 0
                && this.showProductTypesTab
                && this.tabName === 'productTypes'
            ) {
                this.showProductTypeAlertModal = true
                return
            }
            if (this.usedInOtherSurveys) {
                this.showUsedInOtherSurveysModal = true
                return
            }
            await this.persistQuestion(false)
        } catch (error) {
            console.log('saveQuestion ', error)
            this.saveDisabled = false
        }
    }

    saveRespondents = async (questionId) => {
        const store = this
        const app = this.appStore
        const isValid = this.validateRespondents(store.questionRespondents)
        if (isValid) {
            app.http.post(`/question/${questionId}/respondents`, JSON.stringify({
                respondents: store.questionRespondents,
            }))
                .then((response) => {
                    console.log(response)
                })
                .catch((e) => {
                    console.log(e)
                })
        }
    }

    dataToQuestion = (data) => {
        let question
        switch (data.questionTypeCode) {
            case questionType.MultipleChoice:
                question = new MultipleChoiceQuestion(data)
                break
            case questionType.CountryList:
                question = new CountryListQuestion(data)
                break
            case questionType.NumberRange:
                question = new NumericQuestion(data)
                break
            case questionType.Priority:
                question = new PriorityQuestion(data)
                break
            case questionType.Percent:
                question = new PercentageQuestion(data)
                break
            case questionType.SingleChoice:
                question = new SingleChoiceQuestion(data)
                break
            case questionType.Ratings:
                question = new RatingQuestion(data)
                break
            case questionType.DropdownList:
                question = new DropdownListQuestion(data)
                break
            case questionType.StaticText:
                question = new StaticText(data)
                break
            case questionType.TextArea:
                question = new TextAreaQuestion(data)
                break
            case questionType.Grid:
                question = new GridQuestion(data)
                break
            case questionType.SmartGrid:
                question = new SmartGridQuestion(data)
                this.smartGridRows = new OptionSet(this.appStore.questionDetails.smartGrid || {})
                break
            case questionType.TextBox:
                question = new TextBoxQuestion(data)
                break
            case questionType.UrlVariable:
                question = new UrlVariableQuestion(data)
                break
            case questionType.TextBoxList:
                question = new TextBoxListQuestion(data)
                break
            case questionType.Form:
                question = new FormQuestion(data)
                break
            case questionType.Date:
                question = new DateQuestion(data)
                break
            case questionType.NumberForm:
                question = new NumberFormQuestion(data)
                break
            default:
                question = null
                break
        }

        return question
    }

    populateQuestion = async (questionId) => {
        const that = this
        // use questionMenu contextId (1) if editing a division/portfolio/client question in a template
        this.contextOid = this.appStore.isTemplate ? 1 : this.contextOid

        try {
            const questionResponse = await that.appStore.http.get(`/api/question/${questionId}/${this.contextOid}`)
            const { editMode, question: questionRes } = questionResponse

            if (editMode === 'None') {
                this.appStore.addNotification(null, `No rights to edit question ${questionId}`, 'error')
                return undefined
            }

            // below follows if editMode is 'Edit'
            const question = this.dataToQuestion(questionRes)
            if (question.questionTypeCode === 'FM' || question.questionTypeCode === 'NF') {
                question.optionSet.forEach((option) => {
                    this.createSubQuestionStore(
                        option.question,
                        option.renderKey,
                        option.question.questionTypeCode,
                        undefined,
                        option.mandatory,
                    )
                })
            }
            if (question.questionTypeCode === 'SG') {
                question.optionSetY = this.smartGridRows
            }

            question.editMode = questionResponse.editMode
            this.selectedCategory = R.pathOr([], ['questionDetails', 'questionCategories'], that.appStore).find(
                (category) => category.value === question.metadata.categoryId,
            )
            let hasLiveResponses = question.metadata.oid <= 5

            if (question.metadata.oid > 5 && this.appStore.questionDetails.pageQuestionId) {
                const hasLiveResponse = await that.appStore.http.get(`/questions/${questionId}/hasliveresponses`)
                hasLiveResponses = hasLiveResponse.hasLiveResponses
            }

            if (this.usedInOtherSurveys) {
                const otherSurveysResponse = await that.appStore.http.get(`/questions/${questionId}/otherSurveysUsed?surveyId=${this.appStore.survey.id}`)
                that.listOfOtherSurveys = otherSurveysResponse.otherSurveys
            }
            question.liveResponses = hasLiveResponses && question.editMode !== 'Clone'
            return question
        } catch (e) {
            console.error('Question Builder populateQuestion ', e)
            that.appStore.addNotification(null, `Error loading question ${questionId}`, 'error')
            return undefined
        }
    }

    closeModal = () => {
        this.appStore.showQuestionEditor = false
    }

    getPageQuestion = (pageQuestionId, pageIndex) => {
        if (Number.isNaN(pageIndex)) return [{}, {}]

        const page = this.appStore.survey.pages[pageIndex]
        const questionIndex = page.questions.findIndex(
            (pq) => pq.id === pageQuestionId,
        )
        const pageQuestion = page.questions[questionIndex]
        return [pageQuestion, questionIndex]
    }

    hasValidTriggetCondition = (trigger, pageQuestions) => {
        const hasParent = trigger.condition.inheritedPageQuestionId
        const pageQuestion = hasParent
            ? pageQuestions.get(trigger.condition.inheritedPageQuestionId)
            : pageQuestions.get(trigger.condition.pageQuestionId)

        if (!pageQuestion) {
            console.error('No pageQuestion found for trigger', trigger)
            return false
        }

        const { question } = pageQuestion

        if (!question) {
            console.error('No question found for pageQuestion trigger', pageQuestion, trigger)
            return false
        }

        /*
        * This condition is valid as long as the pageQuestion as question exists
        */
        if (trigger.condition.kind === 'QuestionExistsInSurveyResponse') return true

        return hasParent
            ? question.hasOptions(trigger.condition.set1, trigger.condition.set2)
            : question.hasOptions(trigger.condition.set1, trigger.condition.set2, pageQuestions.get(trigger.condition.pageQuestionId))
    }

    isSkipLogicvalid(skipLogic) {
        let isValid = true
        console.log(`isSkipLogicvalid option -> ${JSON.stringify(skipLogic, null, 4)}`)
        if (skipLogic) {
            const pagePriority = this.appStore.survey.pages[this.pageIndex].priority
            const { questionIndex } = this
            const pageQuestions = new Map()
            this.appStore.survey.pages.forEach((page) => {
                if (page.questions && page.priority <= pagePriority) {
                    page.questions.forEach((question, index) => {
                        if (page.priority < pagePriority || index < questionIndex) {
                            pageQuestions.set(question.id, question)
                        }
                    })
                }
            })
            console.log(JSON.stringify(pageQuestions, null, 4))
            skipLogic.triggerGroups.forEach((triggerGroup) => {
                if (triggerGroup.children) {
                    triggerGroup.children.forEach((child) => {
                        if (child.triggers) {
                            child.triggers.forEach((trigger) => {
                                if ((trigger.condition.inheritedPageQuestionId && pageQuestions.has(trigger.condition.inheritedPageQuestionId))) {
                                    console.log(`bad skiplogic ${JSON.stringify(skipLogic, null, 4)}`)
                                }

                                let currentRuleValid = true

                                if (trigger.condition.kind !== 'Quota') {
                                    currentRuleValid = (
                                        pageQuestions.has(trigger.condition.pageQuestionId)
                                        && (
                                            !trigger.condition.inheritedPageQuestionId
                                            || pageQuestions.has(trigger.condition.inheritedPageQuestionId)
                                        )
                                        && this.hasValidTriggetCondition(trigger, pageQuestions)
                                    )
                                }

                                isValid = isValid
                                    && currentRuleValid
                            })
                        }
                    })
                }
            })
            if (!isValid) {
                console.log('invalid skiplogic')
                console.log(JSON.stringify(skipLogic, null, 4))
            }
        }
        return isValid
    }

    getShowQuestionOption = (optionId) => {
        const skipLogic = R.pathOr([], ['pageQuestion', 'skipLogic'])(this)

        return skipLogic.find(({ action }) => (
            action.kind === 'ShowQuestionOption'
            && action.optionId === optionId
        ))
    }

    newShowQuestionOption = (optionId) => new SkipLogic({
        action: {
            kind: 'ShowQuestionOption',
            optionId,
            pageQuestionId: this.pageQuestion.id,

        },
        triggerGroups: [{
            children: [],
            interGroupLogicalOperator: 'OR',
        }],
    }, this.appStore)

    discardSkipLogic = (skipLogic) => {
        console.log(`discardSkipLogic ${JSON.stringify(skipLogic)}`)
        const [pageQuestion, questionIndex] = this.getPageQuestion(
            this.appStore.questionDetails.pageQuestionId, this.pageIndex,
        )

        const pageQuestionSkipLogic = pageQuestion.skipLogic || []
        const index = pageQuestionSkipLogic.findIndex((sl) => sl.id === skipLogic.id)
        if (index > -1) {
            pageQuestionSkipLogic.splice(
                index,
                1,
            )
        }

        console.log('index ->', index, skipLogic.id)
        console.log(JSON.stringify(pageQuestionSkipLogic, null, 4))

        pageQuestion.updateSkipLogic(this.appStore, pageQuestionSkipLogic)
        Reflect.deleteProperty(this.appStore, 'skipLogic')
        this.appStore.showSkipLogic = false
    }

    saveSkipLogic = (skipLogic) => {
        console.log(`saveSkipLogic ${JSON.stringify(skipLogic, null, 4)}`)
        const [pageQuestion] = this.getPageQuestion(
            this.appStore.questionDetails.pageQuestionId, this.pageIndex,
        )
        const pageQuestionSkipLogic = pageQuestion.skipLogic || []
        const hasChildren = skipLogic.triggerGroups.reduce((has, tg) => has || tg.children.length > 0, false)
        let update = false
        if (hasChildren && (pageQuestionSkipLogic.length === 0 || !skipLogic.id)) {
            skipLogic.id = uuidv4()
            console.log('update add new')
            pageQuestionSkipLogic.push(skipLogic)
            update = true
        } else if (hasChildren) {
            console.log('update with children')
            pageQuestionSkipLogic.splice(
                pageQuestionSkipLogic.findIndex((sl) => sl.id === skipLogic.id),
                1,
                skipLogic,
            )
            update = true
        } else if (!hasChildren && skipLogic.id) {
            console.log('update withuot children')
            pageQuestionSkipLogic.splice(
                pageQuestionSkipLogic.findIndex((sl) => sl.id === skipLogic.id),
                1,
            )
            update = true
        }
        console.log(update, hasChildren, pageQuestionSkipLogic.findIndex((sl) => sl.id === skipLogic.id))
        console.log(`saveSkipLogic ${JSON.stringify(pageQuestionSkipLogic, null, 4)}`)

        if (update) {
            pageQuestion.updateSkipLogic(this.appStore, pageQuestionSkipLogic)
        }
        Reflect.deleteProperty(this.appStore, 'skipLogic')
        this.appStore.showSkipLogic = false
    }

    checkSkipLogic = (pageQuestion) => {
        const { question } = this
        let skipLogic = R.pathOr([], ['skipLogic'], pageQuestion).slice()
        if (!skipLogic || skipLogic.length === 0) {
            return
        }

        console.log(`checkSkipLogic ${pageQuestion.id} -> ${JSON.stringify(skipLogic, null, 4)}`)

        let options = []
        if (question.optionSet) {
            options = question.optionSet.options.map((o) => o.id)
        } else if (question.optionSetY) {
            options = [...question.optionSetY.options.map((o) => o.id), ...question.optionSetX.options.map((o) => o.id)]
        }

        skipLogic = skipLogic.filter(({ action }) => (
            action.kind === 'ShowQuestionOption'
            && options.includes(action.optionId)
        ))
        console.log(`checkSkipLogic ${JSON.stringify(skipLogic, null, 4)}`)

        pageQuestion.updateSkipLogic(this.appStore, skipLogic)
    }

    builderRefreshQuestion = (response, questionTypeCode, cloneQuestion) => {
        console.log(JSON.stringify(this.appStore.questionDetails, null, 4))
        const { questionDetails } = this.appStore
        if (questionDetails.editQuestion) {
            questionDetails.editQuestion({
                newId: response.newId,
                questionId: response.originalId,
                title: this.question.content.getTitleByLocale('shortTitle', this.defaultLocale, 'en_GB'),
                questionRespondents: this.questionRespondents,
                category: this.selectedCategory ? this.selectedCategory.label : '',
            })
            return
        }
        const [pageQuestion] = this.getPageQuestion(
            this.appStore.questionDetails.pageQuestionId, this.pageIndex,
        )

        this.checkSkipLogic(pageQuestion)

        const { newId, originalId } = response
        if (newId) {
            pageQuestion.questionId = response.newId
            const isEditingTemplateQuestion = this.appStore.isTemplate && newId === originalId
            if (!isEditingTemplateQuestion) {
                pageQuestion.questionLevel = 'Product'
                pageQuestion.usedInOtherSurveys = false
            }
        }

        if (newId === originalId && newId === questionDetails.questionId) { // not a clone
            pageQuestion.usedInOtherSurveys = questionDetails.usedInOtherSurveys
        }

        const hasParentId = pageQuestion.parentId || (this.parentId && this.parentId.value !== '00000000-0000-0000-0000-000000000000')
        if (!hasParentId) this.setInheritQuestion()
        const parentIdsDiffer = pageQuestion.parentId !== this.parentId?.value
        const isClonedQuestion = response.action === 'QuestionCloned' || cloneQuestion

        if (isClonedQuestion || (hasParentId && parentIdsDiffer)) {
            const options = Object.assign({}, pageQuestion.getCurrentOptions(), {
                parentId: this.parentId.value,
            })
            pageQuestion.parentId = this.parentId.value
            pageQuestion.update(options)
        }

        pageQuestion.refresh(this.appStore)
        if (questionTypeCode === 'SG') {
            pageQuestion.updateSmartGrid(this.appStore, this.smartGridRows)
        }
    }

    qlistAddQuestion = () => {
        eventEmitter.emit('qlist', {
            action: 'addQuestion',
            questionId: this.question.questionId,
            title: this.question.langs.get(this.selectedLocale).T.text,
        })
    }

    qlistRefreshQuestion = (response) => {
        eventEmitter.emit('qlist', {
            action: 'updateQuestion',
            questionId: this.question.questionId,
        })
    }

    // changeDashboardRange = (e) => {
    //     const target = e.target.getAttribute('target')
    //     const label = e.target.getAttribute('label')
    //     const { value } = e.target
    //     console.log(`changeDashboardRange ${value} ${label} ${target}`)
    //     if (target && label) {
    //         if (!this.question.settings.report.dashboardBarRanges[label]) {
    //             this.question.settings.report.dashboardBarRanges[label] = {}
    //         }
    //         this.question.settings.report.dashboardBarRanges[label][target] = value
    //     }
    // }

    addToQlist = (question) => {
        const { survey } = this.appStore
        const body = {
            questionId: question.questionId,
            clientId: this.appStore.tokenData.clientId,
            oid: 6,
            recordId: survey.metadata.productInfo.productId,
            producttypecode: survey.metadata.productInfo.industrySpecificInfo.productType,
            surveytypecode: survey.metadata.taxonomy.surveyType,
            respondenttypecode: survey.metadata.taxonomy.respondentType,
            mandatory: 1,
            surveymandatory: 0,
            priority: 0,
        }
        console.log('inserting to qlist')
        console.log(JSON.stringify(body, null, 4))
        this.appStore.http.post('/qlist/update', JSON.stringify(body))
            .then(() => {})
            .catch((e) => {
                console.log(`Error inserting question to qlist ${e}`)
            })
    }

    builderAddQuestion = () => {
        const question = {}
        question.questionId = this.question.questionId
        question.isMandatory = this.question.metadata.isMandatory
        question.questionTypeCode = this.question.questionTypeCode
        question.oid = this.question.metadata.oid

        if (this.appStore.questionDetails.addQuestion) {
            this.appStore.questionDetails.addQuestion({
                questionId: this.question.questionId,
                title: this.question.content.shortTitle.get(this.defaultLocale).text,
                questionRespondents: this.questionRespondents,
                category: this.selectedCategory ? this.selectedCategory.label : '',
                smartRows: this.question.questionTypeCode === 'SG' ? this.smartGridRows : undefined,
                kpiType: this.question.metadata.kpiType,
            })
            return
        }

        if (this.parentId) {
            question.inheritQuestion = this.parentId
        }
        this.addToQlist(question)
        this.appStore.survey.pages[this.pageIndex].addQuestion({
            question,
            survey: this.appStore.survey,
            store: this.appStore,
            smartRows: this.question.questionTypeCode === 'SG' ? this.smartGridRows : undefined,
        })
    }

    setMergeFields = () => {
        const { appStore, survey } = this
        const mergeFields = [
            {
                code: 'eventdates',
                title: i18n.t('merge_field_eventdates'),
                type: 'main',
            },
            {
                code: 'eventyear',
                title: i18n.t('merge_field_eventyear'),
                type: 'main',
            },
            {
                code: 'surveytitle',
                title: i18n.t('merge_field_surveytitle'),
                type: 'main',
            },
            {
                code: 'productname',
                title: i18n.t('merge_field_productname'),
                type: 'main',
            },
        ]
        console.log('setMergeFields')

        if (!appStore.isQuestionMenu) {
            survey.pages.map(({ questions, pageType }, index) => {
                if (index > this.pageIndex || pageType !== 'ST') {
                    return
                }
                if (questions) {
                    questions.forEach((pageQuestion, questionIndex) => {
                        if (index === this.pageIndex && questionIndex >= this.questionIndex) {
                            return
                        }
                        const { id, question } = pageQuestion
                        const { questionTypeCode, metadata } = question
                        const { initialLocale } = metadata
                        const { defaultLocale } = survey.metadata

                        if (['RD', 'CH', 'SB', 'I', 'TA', 'TX', 'DT'].includes(questionTypeCode)) {
                            mergeFields.push({
                                code: `PQ | ${id}`,
                                title: pageQuestion.questionTitle(appStore),
                                type: 'pipeAnswer',
                            })
                        } else if (['TB', 'SG'].includes(questionTypeCode)) {
                            const questionTitle = pageQuestion.questionTitle(appStore)
                            question.optionSetY.options.forEach((o) => {
                                mergeFields.push({
                                    code: `PQ | ${id}_${o.id}`,
                                    title: `${questionTitle} - ${o.getTitle(this.selectedLocale, defaultLocale, initialLocale)}`,
                                    type: 'pipeAnswer',
                                })
                            })
                        } else if (['PC', 'TL', 'UV'].includes(questionTypeCode)) {
                            const questionTitle = pageQuestion.questionTitle(appStore)
                            question.optionSet.options.forEach((o) => {
                                mergeFields.push({
                                    code: `PQ | ${id}_${o.id}`,
                                    title: `${questionTitle} - ${o.getTitle(this.selectedLocale, defaultLocale, initialLocale)}`,
                                    type: 'pipeAnswer',
                                })
                            })
                        }
                    })
                }
            })
        }

        console.log(`setMergeFields ${JSON.stringify(mergeFields, null, 4)}`)
        this.mergeFields = mergeFields
    }

    changeLanguage = (langObj) => {
        this.lang = langObj
        this.selectedLocale = langObj.value || this.defaultLocale
    }

    setContentText = (value, target, q) => {
        const question = q || this.question
        if (this.sgDisable) {
            return
        }
        const lang = this.selectedLocale
        if (question.content[target]) {
            if (question.content[target].has(lang)) {
                question.content[target].get(lang).text = value
            } else if (value !== '') {
                question.content[target].set(lang, new QuestionLang({ text: value }))
            }
        } else {
            question.content[target] = new Map()
            question.content[target].set(lang, new QuestionLang({ text: value }))
        }
    }

    validate = () => {
        const { question } = this
        let isValid = false

        if (question.content.shortTitle) {
            question.content.shortTitle.forEach((value) => {
                isValid = isValid || Boolean(value.text)
            })
        }

        // Update shortTitle state only if it's available
        if (R.hasPath(['content.shortTitle'], this.regFields)) {
            if (isValid) this.regFields['content.shortTitle']({ value: 'value' }, true)
            else {
                this.regFields['content.shortTitle']({ value: undefined }, true)
            }
        }

        if (!isValid) {
            this.appStore.addNotification(null, i18n.t('q_edit_form_title_validation'), 'error')
        }
        return isValid
    }

    getOptionSet = R.pipe(
        R.either(
            R.prop('optionSet'),
            R.prop('optionSetY'),
        ),
        R.defaultTo([]),
    )

    convertQuestionContent = ({ sourceType, destinationType }) => {
        // in ST (TextBlock) questions, content is displayed instead of longTitle
        if (questionType.StaticText === sourceType) {
            const content = this.getContentText('content')
            this.setContentText(content, 'longTitle')
        } else if (questionType.StaticText === destinationType) {
            const longTitle = this.getContentText('longTitle')
            this.setContentText(longTitle, 'content')
        }
    }

    changeType = (typeCode) => {
        this.smartGridRows = undefined
        const defaultSettings = {
            frontend: {},
            report: {},
        }
        const settings = R.hasPath(['settings'], this.question)
            ? Object.assign(defaultSettings, this.question.settings)
            : defaultSettings

        const optionSet = this.getOptionSet(this.question)
        // reset hasNumericCodes to false to hide numeric input in option editor after question type change
        if (![questionType.NumberForm, questionType.Form].includes(typeCode)
            && R.pathOr(false, ['options'], optionSet)) {
            optionSet.hasNumericCodes = false
            optionSet.options.forEach((option, ind) => (
                delete optionSet.options[ind].reportingValue
            ))
        }

        if ([questionType.NumberForm, questionType.Form].includes(typeCode)) {
            // change type from Other (exclud. NF & FM) to FORM
            // to add sub question to the available options when changing question type to CF or NF
            this.subQuestionMap = observable.map({})
            const formSet = []
            const defaultSubQueType = (typeCode === questionType.NumberForm)
                ? questionType.NumberRange : questionType.DropdownList
            R.pathOr(optionSet, ['options'], optionSet).forEach((option) => {
                const text = option.getTitle(this.selectedLocale, this.defaultLocale, this.question.metadata.initialLocale)
                this.createSubQuestionStore(undefined, option.renderKey, defaultSubQueType, text, option.mandatory || false)
                formSet.push({
                    renderKey: option.renderKey,
                    mandatory: this.subQuestionMap.get(option.renderKey).isMandatory,
                    fieldType: this.subQuestionMap.get(option.renderKey).type,
                    question: this.subQuestionMap.get(option.renderKey).store.question,
                })
            })
            this.question.optionSet = formSet
        } else if ([questionType.NumberForm, questionType.Form].includes(this.question.questionTypeCode)) {
            // change type from FORM to Other
            const setKey = this.question.questionTypeCode === 'TB' ? 'optionSetX' : 'optionSet'
            const newOptions = this.question.optionSet.map((option) => {
                const text = option.getTitle(this.selectedLocale, this.defaultLocale, this.question.metadata.initialLocale)
                const newOption = {
                    allowEdit: true,
                    langs: {},
                }
                newOption.langs[this.selectedLocale] = { text }
                return newOption
            })
            this.question[setKey] = { options: newOptions }
        }

        this.convertQuestionContent({ sourceType: this.question.questionTypeCode, destinationType: typeCode })

        switch (typeCode) {
            case questionType.MultipleChoice:
                this.question = new MultipleChoiceQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = true
                break
            case questionType.CountryList:
                this.question = new CountryListQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = true
                break
            case questionType.NumberRange:
                this.question = new NumericQuestion({
                    metadata: this.question.metadata,
                    content: this.question.content,
                    settings,
                })
                this.question.metadata.isMandatory = true
                break
            case questionType.Priority:
                this.question = new PriorityQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = true
                break
            case questionType.Percent:
                this.question = new PercentageQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = true
                break
            case questionType.SingleChoice:
                this.question = new SingleChoiceQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = true
                break
            case questionType.Ratings:
                this.question = new RatingQuestion({
                    metadata: this.question.metadata,
                    optionSetY: this.question.optionSet || this.question.optionSetY || {},
                    optionSetX: {
                        options: [
                            {
                                priority: 1,
                                langs: {
                                    en_GB: {
                                        text: '(1)',
                                    },
                                },
                            },
                            {
                                priority: 2,
                                langs: {
                                    en_GB: {
                                        text: '(2)',
                                    },
                                },
                            },
                        ],
                    },
                    content: this.question.content,
                    settings,
                })
                this.question.optionSetY.axis = 'y'
                this.question.optionSetX.axis = 'x'
                this.question.metadata.isMandatory = true
                break
            case questionType.DropdownList:
                this.question = new DropdownListQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = true
                break
            case questionType.StaticText:
                this.question = new StaticText({
                    metadata: this.question.metadata,
                    content: this.question.content,
                    settings,
                })
                this.question.metadata.isMandatory = false
                break
            case questionType.TextArea:
                this.question = new TextAreaQuestion({
                    metadata: this.question.metadata,
                    content: this.question.content,
                    settings,
                })
                this.question.metadata.isMandatory = false
                break
            case questionType.Grid:
                this.question = new GridQuestion({
                    metadata: this.question.metadata,
                    optionSetY: this.question.optionSet || this.question.optionSetY || { axis: 'y' },
                    optionSetX: this.question.optionSetX || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSetY.axis = 'y'
                this.question.optionSetX.axis = 'x'
                this.question.metadata.isMandatory = true
                break
            case questionType.SmartGrid:
                const questionData = {
                    metadata: this.question.metadata,
                    optionSetX: this.question.optionSetX || {},
                    content: this.question.content,
                    settings,
                }
                this.question = new SmartGridQuestion(questionData)
                this.question.optionSetX.axis = 'x'
                this.question.metadata.isMandatory = true
                this.smartGridRows = new OptionSet({})
                break
            case 'BEH': case 'COG':
                let sgQuestionData = {
                    metadata: this.question.metadata,
                    optionSetX: this.question.optionSetX || {},
                    content: this.question.content,
                    settings,
                }
                if (
                    [
                        contextOidLevel.QLIST_CLIENT, contextOidLevel.QLIST_DIVISION,
                        contextOidLevel.QLIST_PORTFOLIO, contextOidLevel.CUSTOM_CATEGORY,
                    ].includes(this.contextOid)
                    && ['BEH', 'COG'].includes(typeCode)
                    && this.sgQuestions) {
                    sgQuestionData = this.sgQuestions[typeCode.toLowerCase()]
                    sgQuestionData.metadata.oid = this.contextOid
                    sgQuestionData.metadata.clientId = this.appStore.tokenData.clientId
                    sgQuestionData.id = undefined
                }
                this.question = new SmartGridQuestion(sgQuestionData)
                this.question.optionSetX.axis = 'x'
                this.question.metadata.isMandatory = true
                this.smartGridRows = new OptionSet({})
                break
            case questionType.TextBox:
                this.question = new TextBoxQuestion({
                    metadata: this.question.metadata,
                    content: this.question.content,
                    settings,
                })
                this.question.metadata.isMandatory = false
                break
            case questionType.UrlVariable:
                this.question = new UrlVariableQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = false
                break
            case questionType.Form:
                this.question = new FormQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || [],
                    content: this.question.content,
                })
                break
            case questionType.NumberForm:
                this.question = new NumberFormQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || [],
                    content: this.question.content,
                })
                break
            case questionType.Date:
                this.question = new DateQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    content: this.question.content,
                    settings,
                })
                break
            case questionType.TextBoxList:
                this.question = new TextBoxListQuestion({
                    metadata: this.question.metadata,
                    id: this.question.id,
                    optionSet: this.question.optionSet || this.question.optionSetY || {},
                    content: this.question.content,
                    settings,
                })
                this.question.optionSet.axis = 'y'
                this.question.metadata.isMandatory = false
                break
            default:
                this.question = null
                break
        }

        this.setObjInMap(this.question, this.valueHolder)
    }

    getQuestionTypes = (contextOid, question, industryTypeCode) => {
        const { clientId } = this.appStore.tokenData

        // if (contextOid && question && industryTypeCode) return []

        let types = Object.values(Enums.questionType)

        if (!question.id) {
            types = types.filter((type) => type !== 'RT')
        }

        if (![473, 1].includes(clientId)) { // hotfix 4551 - Show FM only for MaxBI DEMO account
            types = types.filter((type) => !['FM', 'NF'].includes(type))
        }

        if (contextOid === 7) {
            types = types.filter((type) => type !== 'SG')
            if (!question.id && this.pageIndex > 0) {
                types = types.filter((type) => type !== 'UV')
            }
            if (question.id && ['RD', 'CH', 'SB', 'PC', 'OD', 'TL', 'UV'].includes(question.questionTypeCode)) {
                types = types.filter((type) => ['RD', 'CH', 'SB', 'PC', 'OD', 'TL', 'UV'].includes(type))
            }
        } else if (contextOid < 7 || !this.appStore.useLatestUI) {
            types = types.filter((type) => !['UV'].includes(type))
        }
        if ([3, 5, 8].includes(contextOid) && industryTypeCode === 'CE') {
            let index = types.map((t) => t.value).indexOf('SG')
            const kpis = ['BEH', 'COG']
            kpis.filter((kpi) => !this.sgKpis.includes(kpi))
                .forEach((kpi) => {
                    types.splice(
                        index,
                        0,
                        {
                            value: kpi,
                            label: kpi === 'BEH' ? 'Behavioural Smart Grid' : 'Sentiment Smart Grid',
                        },
                    )
                    index += 1
                })
        }
        return types
    }

    prepopulateOnBlur = async (obj) => {
        if (obj.value) {
            this.optionEditorLoader = true
            await this.setPrepopulate(obj)
            this.optionEditorLoader = false
        } else {
            // reset prepopulate selector
            this.prepopulateSelection = undefined
            this.setValueHolder({ value: undefined, name: 'prepopulate' })

            // reset the option editor with three empty options
            const setKey = this.question.questionTypeCode === 'TB' ? 'optionSetX' : 'optionSet'
            const options = Array(3).fill('').map((text) => ({
                allowEdit: true,
                langs: { [this.selectedLocale]: { text } },
            }))
            this.question[setKey].addOptionBulk(options)
            this.setValueHolder({ value: this.question[setKey], name: setKey })
        }
    }

    setPrepopulate = async (obj) => {
        const { appStore, question } = this
        const availableLocaleVals = this.availableLocales.map((locale) => (locale.value))
        try {
            if (obj.value !== this.prepopulateSelection) {
                this.prepopulateSelection = obj
                if (obj.value === 'CO') {
                    let countryLangs = await appStore.http.get(`/countrylangs?langcodes=${[availableLocaleVals].join(',')}`)
                    countryLangs = countryLangs.reduce(
                        (map, { countrycode, langcode, langvalue }) => {
                            if (!Reflect.has(map, countrycode)) {
                                map[countrycode] = {}
                            }
                            map[countrycode][langcode] = langvalue
                            return map
                        }, {},
                    )
                    const optionSet = this.question.questionTypeCode === 'TB' ? question.optionSetX : question.optionSet
                    const options = []
                    Object.keys(countryLangs).forEach((countryKey) => {
                        const option = {
                            langs: {},
                            allowEdit: true,
                        }
                        option.langs = availableLocaleVals.reduce((optObj, locale) => {
                            if (locale === this.defaultLocale
                                || countryLangs[countryKey][locale]
                                || (countryLangs[countryKey][this.defaultLocale]
                                    && countryLangs[countryKey][this.defaultLocale]
                                        !== countryLangs[countryKey].en_GB)) {
                                optObj[locale] = {
                                    text: countryLangs[countryKey][locale]
                                        || countryLangs[countryKey].en_GB,
                                }
                            }
                            return optObj
                        }, {})
                        options.push(option)
                    })
                    optionSet.addOptionBulk(options)
                    this.setValueHolder({
                        value: optionSet, name: this.question.questionTypeCode === 'TB' ? 'optionSetX' : 'optionSet',
                    })
                }
            }
        } catch (error) {
            console.log('setPrepopulate error ', error)
        }
    }

    getLangText = (langs, fieldName) => {
        const {
            appStore, question, selectedLocale, defaultLocale,
        } = this
        const { initialLocale } = question.metadata

        let text = ''
        if (langs.has(selectedLocale)) {
            text = langs.get(selectedLocale).text
            this.fallbackTranslations[fieldName] = undefined
        } else if (langs.has(defaultLocale)) {
            text = langs.get(defaultLocale).text
            this.fallbackTranslations[fieldName] = i18n.t('q_fallback_translation_survey_primary', {
                targetLocale: appStore.localeOptions.find((l) => l.value === selectedLocale)?.label || selectedLocale,
                usedLocale: appStore.localeOptions.find((l) => l.value === defaultLocale)?.label || defaultLocale,
            })
        } else if (langs.has(initialLocale)) {
            text = langs.get(initialLocale).text
            this.fallbackTranslations[fieldName] = i18n.t('q_fallback_translation_question_initial', {
                targetLocale: appStore.localeOptions.find((l) => l.value === selectedLocale)?.label || selectedLocale,
                usedLocale: appStore.localeOptions.find((l) => l.value === initialLocale)?.label || initialLocale,
            })
        }
        if (text === '' && !this.fallbackTranslations.fieldsToHideBlankTranslation.includes(fieldName)) {
            this.fallbackTranslations[fieldName] = i18n.t('q_fallback_translation_blank', {
                targetLocale: appStore.localeOptions.find((l) => l.value === selectedLocale)?.label || selectedLocale,
            })
        }
        return text
    }

    setLangText = (map, value) => {
        const { selectedLocale } = this
        if (map.has(selectedLocale)) {
            map.get(selectedLocale).text = value
        } else {
            map.set(
                selectedLocale,
                new QuestionLang({ text: value }),
            )
        }
        return map
    }

    getPlaceholderValue = (source) => {
        const langs = R.pathOr(new Map(), ['settings', 'frontend', 'placeHolder', leftRight[source]])(this.question)
        return this.getLangText(langs)
    }

    setPlaceHolder = (beforeE, afterE) => {
        let [leftValue, rightValue] = this.question.settings.frontend.placeHolder

        if (!R.isNil(beforeE)) {
            leftValue = this.setLangText(leftValue, beforeE.value)
        } else if (!R.isNil(afterE)) {
            rightValue = this.setLangText(rightValue, afterE.value)
        }

        this.question.settings.frontend.placeHolder = [leftValue, rightValue]
    }

    setInfo = ({ value, name }) => {
        let infoValue = R.hasPath(name.split('.'))(this.question)
            ? R.path(name.split('.'))(this.question)
            : new Map()

        infoValue = this.setLangText(infoValue, value)

        lodash.set(this.question, name, infoValue)
    }

    getInfoValue = () => {
        const langs = R.pathOr(new Map(), ['settings', 'frontend', 'info'])(this.question)
        return this.getLangText(langs)
    }

    setSwitchValue = ({ value, name }) => {
        let switchVal = new Map()
        switchVal = this.setLangText(switchVal, value)
        this.question.settings.frontend.switch[name.includes('0') ? leftRight.left : leftRight.right] = switchVal
        this.setValueHolder({ value: (name.includes('0') ? leftRight.left : leftRight.right), name })
    }

    getSwitchValue = (source) => {
        const langs = R.pathOr(new Map(), ['settings', 'frontend', 'switch', leftRight[source]])(this.question)
        return this.getLangText(langs)
    }

    getSwitchFormat = (options) => {
        let opt = options
            .find(({ value }) => (
                value.left === this.getSwitchValue('left')
                    && value.right === this.getSwitchValue('right')
            ))
        if (!opt) {
            opt = options.find(({ value }) => value.value === '')
            opt = {
                ...opt,
                value: {
                    ...opt.value,
                    left: this.getSwitchValue('left'),
                    right: this.getSwitchValue('right'),
                },
            }
        }
        return opt
    }

    setSwitchFormat = ({ value, name, label }) => {
        this.switchFormat = { value, label }
        this.question.settings.frontend.switch = [value.left, value.right]
        this.setSwitchValue({ value: value.left, name: `${leftRight.left}` })
        this.setSwitchValue({ value: value.right, name: `${leftRight.right}` })
    }

    changeNonRequiredText = ({ value }) => {
        const { selectedLocale } = this
        const nonRequiredPath = this.getNonRequiredLocation()
        if (!nonRequiredPath.nonRequiredText) nonRequiredPath.nonRequiredText = {}
        nonRequiredPath.nonRequiredText[selectedLocale] = value
    }

    getNonRequiredLocation = () => {
        const { question } = this
        if (question.questionTypeCode === 'NF') return R.path(['settings'], question)
        if (question.questionTypeCode === 'FM') return R.path(['settings', 'nonRequired'], question)
        return R.path(['settings', 'frontend', 'nonRequired'], question)
    }

    getNonRequiredText = () => {
        const { question, selectedLocale, defaultLocale } = this
        const { initialLocale } = question.metadata
        const nonRequiredPath = this.getNonRequiredLocation()
        const langs = R.pathOr({}, ['nonRequiredText'], nonRequiredPath)

        let value = ''
        if (langs[selectedLocale]) value = langs[selectedLocale]
        else if (langs[defaultLocale]) value = langs[defaultLocale]
        else if (langs[initialLocale]) value = langs[initialLocale]
        return value
    }

    setFix = ({ value, name }) => {
        let fix = R.hasPath(name.split('.'))(this.question)
            ? R.path(name.split('.'))(this.question)
            : new Map()

        fix = this.setLangText(fix, value)

        lodash.set(this.question, name, fix)
    }

    getFix = (target) => {
        const langs = R.pathOr(new Map(), ['settings', 'frontend', target])(this.question)
        return this.getLangText(langs)
    }

    getContentText = (title, escape = true) => {
        if (!this.question.content[title]) return ''
        const content = this.getLangText(this.question.content[title], title)
        return escape ? unescapeHtmlTags(content) : content
    }

    setField = ({ value, name, label }, type) => {
        switch (type) {
            case 'common':
                lodash.set(this.question, name, value)
                break
            case 'number':
                lodash.set(this.question, name, Number(value))
                break
            case 'date':
                lodash.set(this.question, name, moment(value).format('YYYY-MM-DD'))
                break
            default:
                break
        }
    }

    setValueHolder = ({ value, name }) => {
        const [optionSetKey, propName] = name.split('.')
        if (!['optionSet', 'optionSetY', 'optionSetX'].includes(optionSetKey)) {
            this.valueHolder.set(name, value)
            return
        }
        // set optionSet
        if (!propName) {
            this.valueHolder.set(name, lodash.clone(value))
        } else {
            // set optionSet properties
            const optionSetVal = this.valueHolder.get(optionSetKey)
            optionSetVal[propName] = value
            this.valueHolder.set(optionSetKey, lodash.clone(optionSetVal))
        }
    }

    getValueHolder = (name, type) => {
        const value = this.valueHolder.get(name)
        if (type === 'date') return value && moment(value)
        if (type === 'number') return value && Number(value)
        return value
    }

    toggleEstimateSwitch = (e) => {
        const { value: checked } = e
        const { question, selectedLocale } = this
        const newValue = checked
            ? [
                new Map([[selectedLocale, new QuestionLang({ text: i18n.t('q_switch_format_opt_exact') })]]),
                new Map([[selectedLocale, new QuestionLang({ text: i18n.t('q_switch_format_opt_estimate') })]]),
            ]
            : undefined
        question.settings.switch = newValue

        if (!checked) this.toggleNumberFormNonRequired({ value: false })
    }

    toggleNumberFormNonRequired = (e) => {
        const { value: checked } = e
        this.question.settings.nonRequired = checked
        if (!checked) this.changeNonRequiredText({ value: undefined })
    }

    setObjInMap = (obj, map, parentKey = '') => {
        Object.keys(obj).forEach((key) => {
            const value = obj[key]
            if (['optionSet', 'optionSetY', 'optionSetX'].includes(key)) {
                if (R.pathOr(false, ['options'], obj[key])) {
                    const options = value.options.map((option) => (
                        this.setSkipLogicStatus(option)
                    ))
                    value.options = options
                }
                map.set(key, value)
            } else if (typeof value === 'object' && !isObservableArray(value)) {
                const k = parentKey ? `${parentKey}.${key}` : key
                if (k !== 'store') this.setObjInMap(value, map, k)
            } else {
                const mapKey = parentKey ? `${parentKey}.${key}` : key
                map.set(mapKey, value)
            }
        })
    }

    changeSorting = ({ value, name }) => {
        const [optionSetKey, sortKey] = name.split('.')
        this.question[optionSetKey].changeSorting(sortKey, value)
        this.setValueHolder({ value: this.question[optionSetKey], name: optionSetKey })
    }

    changeEditField = (fieldName) => {
        this.editField = fieldName
    }

    toggleEditModal = () => {
        this.question.overrideEdit = false
        this.showEditModal = !this.showEditModal
    }

    toggleOptionEditModal = (index, name) => {
        if (!this.showOptionEditModal) {
            this.optInd = index
            this.optKey = name
        }
        this.showOptionEditModal = !this.showOptionEditModal
    }

    toggleShowProductTypeModal = () => {
        this.showProductTypeAlertModal = !this.showProductTypeAlertModal
    }

    toggleShowUsedInOtherSurveysModal = () => {
        this.showUsedInOtherSurveysModal = !this.showUsedInOtherSurveysModal
    }

    handleOptionEdit = () => {
        const optionSet = this.getValueHolder(this.optKey)
        optionSet.options[this.optInd].allowEdit = true
        this.setField({ value: optionSet, name: this.optKey }, 'common')
        this.setValueHolder({ value: optionSet, name: this.optKey })
    }

    alterOverrideEdit = (overrideEdit) => {
        this.question.overrideEdit = overrideEdit
        this.setValueHolder({ value: overrideEdit, name: 'overrideEdit' })
    }

    hasSkipLogic = (optionId) => (Boolean(this.getShowQuestionOption(optionId)))

    checkSkipLogicValid = (optionId) => {
        const skipLogic = this.getShowQuestionOption(optionId)
        return skipLogic
            ? this.isSkipLogicvalid(skipLogic)
            : true
    }

    toggleSkipLogic = (optionId, optionText, optionSetName) => {
        const skipLogic = this.getShowQuestionOption(optionId)
            || this.newShowQuestionOption(optionId)

        this.appStore.skipLogic = this.appStore.survey.copySkipLogic(
            JSON.parse(JSON.stringify(skipLogic)),
        )
        this.appStore.skipLogicConfig = {
            pageIndex: this.pageIndex,
            questionIndex: this.questionIndex,
            page: null,
            option: optionId,
            type: 'questionOption',
            title: optionText,
            question: null,
            onSave: this.saveSkipLogic,
            onDiscard: this.discardSkipLogic,
            optionSetName,
        }

        this.appStore.showSkipLogic = true
    }

    // showSkipLogicIcon = (optionId) => (
    //     optionId && this.hasSkipLogic(optionId) && (this.questionIndex > 0 || this.pageIndex > 0)
    // )

    findSkipLogicStatus = (optionId) => {
        if (!this.hasSkipLogic(optionId)) return skipLogicStatus.noSkipLogic
        return this.checkSkipLogicValid(optionId)
            ? skipLogicStatus.validSkipLogic : skipLogicStatus.invalidSkipLogic
    }

    setSkipLogicStatus = (option) => {
        const status = this.findSkipLogicStatus(option.id)
        if (status === skipLogicStatus.noSkipLogic) {
            option.validSkipLogic = undefined
        } else {
            option.validSkipLogic = (status === skipLogicStatus.validSkipLogic)
        }
        return option
    }

    addOption = (option, setKey, justAdded) => {
        if ([questionType.NumberForm, questionType.Form].includes(this.question.questionTypeCode)) {
            const defaultSubQueType = this.question.questionTypeCode === questionType.NumberForm
                ? questionType.NumberRange : questionType.DropdownList
            const renderKey = uuidv4()
            this.createSubQuestionStore(undefined, renderKey, defaultSubQueType, '')
            const formOption = {
                renderKey,
                mandatory: this.subQuestionMap.get(renderKey).isMandatory,
                fieldType: this.subQuestionMap.get(renderKey).type,
                question: this.subQuestionMap.get(renderKey).store.question,
            }
            this.question.addOption(formOption, justAdded)
            this.setValueHolder({ value: this.question.optionSet, name: 'optionSet' })
            return
        }

        this.question[setKey].addOption(option, justAdded)
        this.setValueHolder({ value: this.question[setKey], name: setKey })
    }

    /** Logic for Header rows */
    addHeader = () => {
        const { question } = this
        question.addHeaderGroup()
    }

    showHeaderRow = () => {
        const { question, appStore } = this
        const { questionTypeCode } = question

        return (
            appStore.isExploriAdmin()
            && ['RD', 'SB', 'CH', 'TB', 'PC'].includes(questionTypeCode)
        )
    }

    getHeaderTitle = (langs) => {
        const { selectedLocale, defaultLocale, question } = this
        const { initialLocale } = question.metadata

        let value = ''
        if (langs.has(selectedLocale)) {
            value = langs.get(selectedLocale)
        } else if (langs.has(defaultLocale)) {
            value = langs.get(defaultLocale)
        } else if (langs.has(initialLocale)) {
            value = langs.get(initialLocale)
        }

        return value
    }

    setHeaderTitle = (index, value) => {
        const { selectedLocale, question } = this
        const headers = question.headerGroups.slice()
        const header = headers[index]
        header.title.set(selectedLocale, value)
        question.headerGroups = headers
    }

    deleteHeader = (index) => {
        const { question } = this
        const headers = question.headerGroups.slice()
        const [deletedHeader] = headers.splice(index, 1)

        question.optionSet.options
            .filter(({ headerGroupId }) => headerGroupId === deletedHeader.id)
            .forEach((option) => {
                Reflect.deleteProperty(option, 'headerGroupId')
            })

        question.headerGroups = headers
    }

    removeHeader = (index) => {
        const { question } = this
        const headers = question.headerGroups.slice()
        const [deletedHeader] = headers.splice(index, 1)

        question.optionSet.options
            .filter(({ headerGroupId }) => headerGroupId === deletedHeader.id)
            .forEach((option) => {
                Reflect.deleteProperty(option, 'headerGroupId')
            })

        question.headerGroups = headers
    }

    getHeaderOptions = () => {
        const { question } = this
        const { headerGroups } = question
        let headerRows = []

        if (headerGroups) {
            headerRows = headerGroups.map(({ title, id }) => ({
                label: this.getHeaderTitle(title),
                value: id,
            }))
        }

        return headerRows
    }

    setHeaderRow = (option, headerRowId) => {
        const { question } = this
        const { headerGroups } = question
        const { id } = option

        headerGroups.forEach((headerRow) => {
            headerRow.options = headerRow.options.filter((optionId) => optionId !== id)
        })

        if (headerRowId) {
            const headerRow = headerGroups.find(({ id }) => id === headerRowId)
            headerRow.options.push(id)
        }
        option.headerGroupId = headerRowId
    }
    /** End logic for Header rows */

    /** Other piping behaviour */
    getPipingBehaviours = () => ([
        {
            value: 'append',
            label: 'Append',
        },
        {
            value: 'replace',
            label: 'Replace',
        },
    ])

    setPipingBehaviour = (option, behaviour) => {
        option.otherPiping = behaviour
    }
    /** end other piping behaviour */

    createSubQuestionStore = (question, optId, subQueType, text, isMandatory) => {
        if (this.subQuestionMap.has(optId)) return
        const subQuestionBuilderStore = this.initSubQuestionStore(subQueType, question)
        const subQuestion = {
            type: subQueType, store: subQuestionBuilderStore, isMandatory: isMandatory || false,
        }
        this.subQuestionMap.set(optId, subQuestion)
        if (text) this.updateSubQuestionShortTitle(optId, text)
    }

    initSubQuestionStore = (subQueType, question) => {
        const subQuestionBuilderStore = new QuestionBuilderStore(
            this.appStore, this.appStore.survey,
        )
        try {
            subQuestionBuilderStore.initSubQuestion(subQueType, question)
        } catch (error) {
            console.log('initSubQuestionStore error ', error)
        }
        return subQuestionBuilderStore
    }

    toggleSubQueModal = (optId) => {
        if (this.subQueOptionId === undefined) {
            this.subQueOptionId = optId
        } else {
            this.subQueOptionId = undefined
        }
    }

    toggleSubQueMandatory = (optId) => {
        const { store, isMandatory, type } = this.subQuestionMap.get(optId)
        // store.changeType(type)
        const subQuestion = { store, type, isMandatory: !isMandatory }
        this.subQuestionMap.set(optId, subQuestion)
        const option = this.question.optionSet.find((opt) => (opt.renderKey === optId))
        if (option) {
            option.mandatory = !option.mandatory
            this.setValueHolder({
                value: this.question.optionSet, name: 'optionSet',
            })
        }
    }

    updateSubQuestionShortTitle = (optId, text) => {
        const subQuestion = this.subQuestionMap.get(optId)
        subQuestion.store.setContentText(text, 'shortTitle')
        subQuestion.store.setValueHolder({ value: text, name: 'content.shortTitle' })
    }

    updateOptionText = (subQueStore, optId) => {
        const shortTitle = subQueStore.question.content.shortTitle.get(this.selectedLocale).text
        const option = this.question.optionSet.find((opt) => (opt.renderKey === optId))
        if (option) {
            option.question.content.shortTitle.get(this.selectedLocale).text = shortTitle
            this.setValueHolder({
                value: this.question.optionSet, name: 'optionSet',
            })
        }
    }

    remSubQuestion = (optId) => {
        this.subQuestionMap.delete(optId)
    }

    saveSubQuestion = (optId) => {
        const subQueStore = this.subQuestionMap.get(optId).store
        // validate sub question form
        if (!subQueStore.validate()) return
        // get sub question short title and assign it to option -> content -> text
        this.updateOptionText(subQueStore, optId)
        // hide the sub question modal
        this.subQueOptionId = undefined
    }

    changeSubQuestionType = (optId, type) => {
        const { store, isMandatory } = this.subQuestionMap.get(optId)
        store.changeType(type)
        const subQuestion = { store, type, isMandatory }
        this.subQuestionMap.set(optId, subQuestion)
    }

    toggleSkipLogicAlertModal = () => {
        this.showSkipLogicAlertModal = !this.showSkipLogicAlertModal
    }

    toggleAccordion = (index) => {
        this.accState = {
            ...this.accState,
            [index]: !this.accState[index],
        }
    }

    resetSwitch = () => {
        this.switchFormat = undefined
        this.question.settings.frontend.switch = undefined
        this.setValueHolder({ value: undefined, name: 'settings.frontend.switch' })
        this.setValueHolder({ value: undefined, name: 'settings.frontend.switch.0' })
        this.setValueHolder({ value: undefined, name: 'settings.frontend.switch.1' })
    }

    resetInfo = () => {
        if (this.getValueHolder('settings.frontend.info.check')) {
            this.question.settings.frontend.info = undefined
            this.setValueHolder({ value: undefined, name: 'settings.frontend.info' })
        }
    }

    toggleCheckAcc = (index) => {
        this.isAccClose = {
            ...this.isAccClose,
            [index]: !this.isAccClose[index],
        }
    }

    changeTemplate = ({ value, name }) => {
        const val = R.isNil(value) ? undefined : value
        this.question.settings.frontend.template = val
        this.setValueHolder({ value: val, name })
    }

    findParentQuestions = () => {
        const { appStore, pageIndex } = this
        const { survey } = appStore

        const parentQuestions = []
        const parentOptions = {}

        survey.pages.forEach((surveyPage, index) => {
            if (index < pageIndex && surveyPage.pageType === 'ST') {
                surveyPage.questions.forEach((question) => {
                    console.log(JSON.stringify(question, null, 4))
                    if (['I'].includes(question.question.questionTypeCode)) {
                        parentQuestions.push({
                            value: question.id,
                            questionType: question.question.questionTypeCode,
                            label: question.questionTitle(appStore),
                        })
                    } else if (['PC'].includes(question.question.questionTypeCode)) {
                        parentQuestions.push({
                            value: question.id,
                            questionType: question.question.questionTypeCode,
                            label: question.questionTitle(appStore),
                        })
                        const options = []
                        question.question.optionSet.options.forEach((o) => {
                            options.push({
                                value: o.id,
                                label: o.getTitle(
                                    appStore.surveyLocale,
                                    survey.metadata.defaultLocale,
                                    question.question.metadata.initialLocale,
                                ),
                            })
                        })
                        parentOptions[question.id] = options
                    }
                })
            }
        })

        this.parentQuestions = parentQuestions
        this.parentOptions = parentOptions
    }

    changeIgnoreValidation = ({ value }) => {
        this.question.settings.frontend.ignoreTotalValidation = value
        this.setValueHolder({ value, name: 'settings.frontend.ignoreTotalValidation' })

        if (!value) {
            this.question.settings.frontend.min = 0
            this.setValueHolder({ value: 0, name: 'settings.frontend.min' })
        }
    }

    changeMaxInheritOption = (obj) => {
        const { value } = obj
        const [questionId] = this.question.settings.frontend.inheritMax.split('|')

        const optionId = value ? `|${value}` : ''
        const inheritMax = `${questionId}${optionId}`

        this.question.settings.frontend.inheritMax = inheritMax
        this.setValueHolder({ value: inheritMax, name: 'settings.frontend.inheritMax' })
    }

    getSliderLabel = (index) => {
        const { initialLocale } = this.question.metadata

        const langs = R.pathOr(new Map(), ['settings', 'frontend', 'sliderLabels', index])(this.question)

        let sliderLabel = ''

        if (langs.has(this.selectedLocale)) {
            sliderLabel = langs.get(this.selectedLocale)
        } else if (langs.has(this.defaultLocale)) {
            sliderLabel = langs.get(this.defaultLocale)
        } else if (langs.has(initialLocale)) {
            sliderLabel = langs.get(initialLocale)
        }

        return sliderLabel
    }

    setSliderLabel = (index, label) => {
        if (!Array.isArray(R.pathOr(undefined, ['settings', 'frontend', 'sliderLabels'])(this.question))) {
            this.question.settings.frontend.sliderLabels = []
        }

        const sliderLabel = R.hasPath(['settings', 'frontend', 'sliderLabels', index])(this.question)
            ? R.path(['settings', 'frontend', 'sliderLabels', index])(this.question)
            : new Map()

        sliderLabel.set(this.selectedLocale, label)

        this.question.settings.frontend.sliderLabels[index] = sliderLabel
        this.setValueHolder({ value: sliderLabel, name: `settings.frontend.sliderLabels[${index}` })
    }

    toggleMetrics = ({ value }) => {
        this.question.optionSet.hasMetrics = value
    }

    toggleAllowImports = ({ value }) => {
        this.question.settings.report.allowImports = value
    }

    fetchMetrics = async () => {
        try {
            const response = await this.appStore.http.get('/metric')

            this.metrics = response.map((metric) => ({
                label: JSON.parse(metric.value),
                value: metric.id,
            }))
        } catch (e) {
            console.log(e)
        }
    }

    updateOptionMetrics = (index, value, setting) => {
        this.question.optionSet.options[index][setting] = value
    }
}
